import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { homePath, roleLabel } from '../../utils';
import { SidebarUserProfile } from './SidebarUserProfile';

interface NavItem {
  name: string;
  shortName?: string;
  href: string;
  icon: string;
}

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  navigation: NavItem[];
}

export const MobileNav: React.FC<MobileNavProps> = ({ isOpen, onClose, navigation }) => {
  const { logout, accountType } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/login');
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 z-40 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={`fixed inset-y-0 left-0 w-72 max-w-full flex flex-col shadow-2xl transform transition-transform duration-300 ease-out z-50 bg-shell-900 pt-[var(--app-safe-top,env(safe-area-inset-top,0px))] pb-[var(--app-safe-bottom,env(safe-area-inset-bottom,0px))] ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-14 px-5 border-b border-brand-700/50">
          <Link
            to={homePath(accountType)}
            onClick={onClose}
            className="flex items-center gap-2.5 hover:opacity-90 transition-opacity"
          >
            <img src="/favicon.svg" alt="Passport" className="w-7 h-7 rounded-full flex-shrink-0" />
            <div>
              <h1 className="text-white font-bold text-base leading-none">Passport</h1>
              <span className="text-brand-300 text-xs">{roleLabel(accountType)}</span>
            </div>
          </Link>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-brand-300 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navigation.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all gap-3 ${
                  active
                    ? 'bg-white/15 text-white'
                    : 'text-brand-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                <svg className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-300' : 'text-brand-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
                {item.name}
                {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-300" />}
              </Link>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-brand-700/50">
          <SidebarUserProfile onLogout={handleLogout} />
        </div>
      </div>
    </>
  );
};
